import { useEffect, useRef, useState } from "react";
import { ChevronDown, Download, ExternalLink, FileText, Paperclip, Share2 } from "lucide-react";
import Btn from "../Btn";
import { todoTaskApi } from "../../api/todoTaskApi";

/**
 * "PDF ▾" — open, download or SHARE a to-do PDF, with the task's files.
 *
 *  - Open PDF: a new tab (falls back to a download when pop-ups are blocked).
 *  - Download PDF.
 *  - Share: the phone / computer share sheet (WhatsApp, mail…) with the PDF
 *    and — on a task — every attached file, where the browser can share files
 *    (Chrome / Edge on Android and Windows, Safari). Files the browser will not
 *    share (Word, Excel…) are downloaded instead, and it says so; a browser
 *    that cannot share at all (Firefox on a computer) downloads everything.
 *  - Download all files (a task with attachments).
 *
 * `getPdf()` returns `{ blob, name }`. `attachments` are the task's
 * `{ url, originalName, contentType }`.
 */
export default function TodoPdfMenu({ getPdf, attachments = [], shareTitle, shareText, size = "sm", label = "PDF" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  // Share prepared but the browser wanted a fresh tap (it took too long to fetch).
  const [ready, setReady] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const files = attachments.filter((a) => a && a.url);
  const run = async (key, fn) => {
    if (busy) return;
    setBusy(key);
    setNote("");
    try {
      await fn();
    } catch (e) {
      setNote(await blobErrMsg(e, "Could not make the PDF"));
    } finally {
      setBusy("");
    }
  };

  const openPdf = () => run("open", async () => {
    const { blob, name } = await getPdf();
    openBlob(blob, name);
    setOpen(false);
  });

  const downloadPdf = () => run("download", async () => {
    const { blob, name } = await getPdf();
    saveBlob(blob, name);
    setOpen(false);
  });

  const downloadFiles = () => run("files", async () => {
    const { list, failed } = await fetchFiles(files);
    list.forEach((f) => saveBlob(f, f.name));
    setNote(failed.length ? `Could not fetch: ${failed.join(", ")}` : `Downloaded ${list.length} file${list.length === 1 ? "" : "s"}.`);
  });

  const share = () => run("share", async () => {
    setReady(null);
    const { blob, name } = await getPdf();
    const pdf = new File([blob], name, { type: "application/pdf" });
    const { list, failed } = await fetchFiles(files);
    const all = [pdf, ...list];
    // The PDF first, then as many files as the browser takes in one share
    // (it refuses some types — Word, Excel — and too many / too large sets).
    const shareable = [];
    if (canShare([pdf])) {
      shareable.push(pdf);
      list.forEach((f) => { if (canShare([...shareable, f])) shareable.push(f); });
    }
    const other = all.filter((f) => !shareable.includes(f));
    const prepared = { files: shareable, other, failed, title: shareTitle, text: shareText };
    if (shareable.length === 0) {
      // No file sharing in this browser: everything is downloaded to attach by hand.
      all.forEach((f) => saveBlob(f, f.name));
      setNote(`This browser cannot share files — downloaded ${all.length === 1 ? "the PDF" : `the PDF and ${all.length - 1} file${all.length === 2 ? "" : "s"}`}`
        + ` to send from WhatsApp or mail.${failed.length ? ` Could not fetch: ${failed.join(", ")}.` : ""}`);
      return;
    }
    await doShare(prepared);
  });

  // The share itself — straight away, or from the "Share now" tap.
  const doShare = async (prepared) => {
    try {
      await navigator.share({ files: prepared.files, title: prepared.title, text: prepared.text });
      setReady(null);
      finishNote(prepared);
    } catch (e) {
      if (e?.name === "AbortError") { setReady(null); return; }          // closed the share sheet
      if (e?.name === "NotAllowedError") {                               // fetching took too long for the browser
        setReady(prepared);
        setNote("Files are ready — tap Share now.");
        return;
      }
      [...prepared.files, ...prepared.other].forEach((f) => saveBlob(f, f.name));
      setReady(null);
      setNote("Sharing failed — downloaded the files instead.");
    }
  };

  const finishNote = (prepared) => {
    const parts = [];
    if (prepared.other.length) {
      prepared.other.forEach((f) => saveBlob(f, f.name));
      parts.push(`${prepared.other.map((f) => f.name).join(", ")} could not go in the browser's share — downloaded to attach by hand.`);
    }
    if (prepared.failed.length) parts.push(`Could not fetch: ${prepared.failed.join(", ")}.`);
    setNote(parts.join(" "));
    if (!parts.length) setOpen(false);
  };

  const shareLabel = files.length ? `Share PDF + ${files.length} file${files.length === 1 ? "" : "s"}` : "Share PDF";

  return (
    <div ref={boxRef} style={{ position: "relative", display: "inline-block" }}>
      <Btn variant="secondary" size={size} icon={<FileText size={13} />} onClick={() => setOpen((v) => !v)}>
        {label} <ChevronDown size={12} style={{ marginLeft: 2 }} />
      </Btn>
      {open && (
        <div role="menu" style={{
          position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 50, minWidth: 230, padding: 6,
          background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "var(--shadow-md, 0 8px 24px rgba(0,0,0,0.15))",
        }}>
          <Item icon={<ExternalLink size={14} />} busy={busy === "open"} disabled={!!busy} onClick={openPdf}>Open PDF</Item>
          <Item icon={<Download size={14} />} busy={busy === "download"} disabled={!!busy} onClick={downloadPdf}>Download PDF</Item>
          {ready
            ? <Item icon={<Share2 size={14} />} strong disabled={!!busy} onClick={() => doShare(ready)}>Share now</Item>
            : <Item icon={<Share2 size={14} />} busy={busy === "share"} disabled={!!busy} onClick={share}>{shareLabel}</Item>}
          {files.length > 0 && (
            <Item icon={<Paperclip size={14} />} busy={busy === "files"} disabled={!!busy} onClick={downloadFiles}>
              Download {files.length === 1 ? "the file" : `all ${files.length} files`}
            </Item>
          )}
          {note && (
            <div style={{ fontSize: 12, color: "var(--text-1)", padding: "6px 8px 2px", maxWidth: 280, lineHeight: 1.35 }}>{note}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Item({ icon, children, onClick, disabled, busy, strong }) {
  return (
    <button type="button" role="menuitem" onClick={onClick} disabled={disabled} style={{
      display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "7px 9px", border: "none", borderRadius: 7,
      background: strong ? "var(--primary-light)" : "transparent", color: "var(--text-0)", fontSize: 13,
      fontWeight: strong ? 700 : 500, cursor: disabled ? "default" : "pointer", fontFamily: "inherit", textAlign: "left",
      opacity: disabled && !busy ? 0.55 : 1,
    }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = "var(--bg-hover)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = strong ? "var(--primary-light)" : "transparent"; }}>
      {icon}
      <span style={{ flex: 1 }}>{busy ? "Preparing…" : children}</span>
    </button>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

/** "TD/2026/0011" → "TD-2026-0011.pdf". */
export const taskPdfName = (task) => `${String(task?.taskNumber || "task").replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}.pdf`;

/** "todo-assigned-by-me-ravi-patil-2026-09-27.pdf" — the same name the server gives. */
export function listPdfName(viewLabel, person) {
  const slug = (s) => String(s || "").trim().replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").toLowerCase();
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${slug(`todo ${viewLabel}${person ? ` ${person}` : ""}`) || "todo"}-${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}.pdf`;
}

/** Every attached file of a task, oldest first. */
export function taskAttachments(task) {
  const out = [];
  (task?.messages || []).forEach((m) => (m.attachments || []).forEach((a) => out.push(a)));
  return out;
}

function canShare(files) {
  try {
    return typeof navigator !== "undefined" && typeof navigator.share === "function"
      && typeof navigator.canShare === "function" && navigator.canShare({ files });
  } catch {
    return false;
  }
}

/** The files as File objects (names made unique); the ones that could not be fetched by name. */
async function fetchFiles(attachments) {
  const used = new Set();
  const unique = (name) => {
    let n = name || "file";
    if (!used.has(n.toLowerCase())) { used.add(n.toLowerCase()); return n; }
    const dot = n.lastIndexOf(".");
    const base = dot > 0 ? n.slice(0, dot) : n;
    const ext = dot > 0 ? n.slice(dot) : "";
    for (let i = 2; ; i++) {
      const c = `${base} (${i})${ext}`;
      if (!used.has(c.toLowerCase())) { used.add(c.toLowerCase()); return c; }
    }
  };
  const results = await Promise.all(attachments.map(async (a) => {
    try {
      const res = await todoTaskApi.fileBlob(a.url);
      const blob = res.data;
      return { ok: true, a, blob };
    } catch {
      return { ok: false, a };
    }
  }));
  const list = [];
  const failed = [];
  results.forEach((r) => {
    const name = r.a.originalName || "file";
    if (!r.ok) { failed.push(name); return; }
    list.push(new File([r.blob], unique(name), { type: r.blob.type || r.a.contentType || "application/octet-stream" }));
  });
  return { list, failed };
}

export function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name || "file";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function openBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) saveBlob(blob, name);          // pop-up blocked
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** The server's message from a failed blob request (it arrives as a Blob, not JSON). */
async function blobErrMsg(e, fallback) {
  try {
    const data = e?.response?.data;
    if (data instanceof Blob) {
      const txt = await data.text();
      try { return JSON.parse(txt)?.message || fallback; } catch { return txt || fallback; }
    }
    return data?.message || e?.message || fallback;
  } catch {
    return fallback;
  }
}
